import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wallet, TrendingUp, ShieldCheck } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [seedDemo, setSeedDemo] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptTerms) { setError("Bitte stimme den AGB und der Datenschutzerklärung zu."); return; }
    if (password.length < 8) { setError("Passwort muss mindestens 8 Zeichen lang sein."); return; }
    setLoading(true);
    const res = await register({
      email, password, name, company_name: company,
      accept_terms: acceptTerms, seed_demo_data: seedDemo,
    });
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="grid lg:grid-cols-2 flex-1">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-neutral-950 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center font-display font-bold text-neutral-900">
              AC
            </div>
            <div className="font-display text-xl font-semibold">AgencyCash</div>
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-semibold tracking-tighter leading-[1.05] max-w-md">
            Wie viel Geld ist <span className="text-emerald-400">wirklich frei</span> verfügbar?
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            Das Finanz-Cockpit für Agenturen, Freelancer-Teams und Dienstleister. Klare Antworten statt Buchhaltung.
          </p>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          {[
            { icon: Wallet, t: "Kontostand minus Verpflichtungen", d: "Sehe den wirklich freien Betrag, nicht den Bank-Bruttostand." },
            { icon: ShieldCheck, t: "USt-Rücklage automatisch berechnet", d: "Nie wieder Schreck zum Quartalsende." },
            { icon: TrendingUp, t: "Cashflow & Liquiditätsreichweite", d: "Wie viele Monate hält dein Geschäft durch?" },
          ].map(({ icon: I, t, d }) => (
            <div key={t} className="flex gap-3 items-start">
              <I className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold">{t}</div>
                <div className="text-sm text-neutral-400">{d}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-neutral-900 flex items-center justify-center text-white font-bold font-display">AC</div>
            <div className="font-display text-xl font-semibold">AgencyCash</div>
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight mb-2">
            {tab === "login" ? "Willkommen zurück" : "Konto kostenlos erstellen"}
          </h2>
          <p className="text-neutral-500 mb-8">
            {tab === "login"
              ? "Melde dich mit deiner E-Mail und deinem Passwort an."
              : "Eigene Agentur, eigenes Login. Kein Setup, kein Zwang zu Kreditkarte."}
          </p>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Anmelden</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrieren</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Passwort</Label>
                    <Link to="/forgot-password" className="text-xs text-neutral-500 hover:text-neutral-900 underline" data-testid="forgot-password-link">Passwort vergessen?</Link>
                  </div>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                  {loading ? "Wird angemeldet…" : "Anmelden"}
                </Button>
                <p className="text-xs text-neutral-500 text-center">
                  Noch kein Konto? <button type="button" onClick={() => setTab("register")} className="underline hover:text-neutral-900">Hier registrieren</button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="r-name">Dein Name</Label>
                  <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" placeholder="Max Mustermann" />
                </div>
                <div>
                  <Label htmlFor="r-company">Agentur / Firma</Label>
                  <Input id="r-company" value={company} onChange={(e) => setCompany(e.target.value)} required data-testid="register-company" placeholder="z.B. Müller Marketing GmbH" />
                </div>
                <div>
                  <Label htmlFor="r-email">E-Mail</Label>
                  <Input id="r-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" placeholder="du@deine-agentur.de" />
                </div>
                <div>
                  <Label htmlFor="r-password">Passwort <span className="text-xs text-neutral-400">(mind. 8 Zeichen)</span></Label>
                  <Input id="r-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="register-password" />
                </div>

                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="register-demo-toggle">
                  <Checkbox checked={seedDemo} onCheckedChange={(v) => setSeedDemo(!!v)} className="mt-0.5" />
                  <span className="text-neutral-700">Mit <strong>Demo-Daten</strong> starten (1 Konto, 2 Kunden, 1 Projekt — kannst du jederzeit löschen)</span>
                </label>

                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="register-terms-toggle">
                  <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" required />
                  <span className="text-neutral-700">
                    Ich stimme den <Link to="/agb" target="_blank" className="underline">AGB</Link> und der <Link to="/datenschutz" target="_blank" className="underline">Datenschutzerklärung</Link> zu.
                  </span>
                </label>

                {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                  {loading ? "Wird erstellt…" : "Konto kostenlos erstellen"}
                </Button>
                <p className="text-xs text-neutral-500 text-center">
                  Dein eigenes, separates Cockpit. Deine Daten bleiben deine.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    <PublicFooter />
    </div>
  );
}
